import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectPb_email, selectPb_username } from 'store/selectors';
import { globalAPI } from 'services/global.service';
import { Check, DocumentOutline, AppNextLogoMark } from 'assets/icons';
import * as S from './DashboardWelcome.styled';

const DESCRIPTION =
  'Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean commodo ligula eget dolor. Aenean massa. Cum sociis natoque penatibus et magnis dis parturient montes, ridiculus mus. Lorem ipsum dolor sit amet, consectetuer adipiscing elit.';

const GETTING_STARTED_LINKS = [
  { label: "Appnext's Best Practices for Ad Monetization", href: '#' },
  { label: 'Reporting Post-Install Events to Appnext', href: '#' },
  { label: 'Appnext’s New Automatic Rule: Excluding Publisher Apps', href: '#' },
  { label: 'Appnext Ad Units', href: '#' },
];

// Maps the API status to the visual state used by the styled components.
const STATUS_TO_STATE = {
  completed: 'done',
  active: 'active',
  pending: 'todo',
};

// Frontend owns routing: resolve the API's stable actionKey to an internal route.
const ACTION_ROUTES = {
  open_account_settings: '/account-settings',
  open_payments: '/payments',
  create_campaign: '/Discovery/Campaign/New',
};

const getInitials = (username, email) => {
  const source = username || email || '';
  const words = source
    .trim()
    .split(/[\s@._-]+/)
    .filter(Boolean);
  if (!words.length) return '';
  const initials = words.slice(0, 2).map((word) => word[0]);
  return initials.join('').toUpperCase();
};

export const DashboardWelcome = () => {
  const navigate = useNavigate();
  const username = useSelector(selectPb_username);
  const email = useSelector(selectPb_email);

  const { data, isLoading, isError } = globalAPI.useGetSetupStepsQuery();

  const steps = data?.steps ?? [];
  const completedCount = data?.progress?.completedCount ?? 0;

  const handleStepClick = (route) => {
    if (route) navigate(route);
  };

  const initials = getInitials(username, email);
  const displayName = username || '';

  const renderSetup = () => {
    if (isLoading) {
      return <S.StatusText>Loading setup…</S.StatusText>;
    }
    if (isError || !steps.length) {
      return null;
    }

    return (
      <>
        <S.ProgressSection>
          <S.ProgressTitle>
            <strong>Complete</strong>
            <span> Setup</span>
          </S.ProgressTitle>
          <S.ProgressBar>
            {steps.map((step, index) => (
              <S.ProgressSegment key={step.key} $filled={index < completedCount} />
            ))}
          </S.ProgressBar>
        </S.ProgressSection>

        <S.StepList>
          {steps.map((step) => {
            const state = STATUS_TO_STATE[step.status] ?? 'todo';
            const isActive = state === 'active';
            const route = step.cta ? ACTION_ROUTES[step.cta.actionKey] : undefined;

            return (
              <S.Step key={step.key} $state={state}>
                <S.StepRow $align={step.description && isActive ? 'flex-start' : 'center'}>
                  {state === 'done' ? (
                    <S.StepDoneBadge>
                      <Check />
                    </S.StepDoneBadge>
                  ) : (
                    <S.StepNumber $state={state}>{step.order}</S.StepNumber>
                  )}
                  <S.StepContent>
                    <S.StepLabel $muted={!isActive}>{step.title}</S.StepLabel>
                    {step.description && isActive && (
                      <S.StepDescription>{step.description}</S.StepDescription>
                    )}
                  </S.StepContent>
                </S.StepRow>
                {step.cta && isActive && (
                  <S.StepButton type='button' onClick={() => handleStepClick(route)}>
                    {step.cta.label}
                  </S.StepButton>
                )}
              </S.Step>
            );
          })}
        </S.StepList>
      </>
    );
  };

  return (
    <S.Wrap>
      <S.Left>
        <S.BannerWrap>
          <S.Banner>
            <S.BannerContent>
              <S.UserBlock>
                <S.UserName>Welcome, {displayName}!</S.UserName>
                {email && <S.UserEmail>{email}</S.UserEmail>}
              </S.UserBlock>
              <S.Description>{DESCRIPTION}</S.Description>
              <S.PrimaryButton type='button'>Start Product Tour</S.PrimaryButton>
            </S.BannerContent>

            <S.BannerBadge>
              <S.BadgeLogo>
                <AppNextLogoMark />
              </S.BadgeLogo>
              {initials && <S.Avatar>{initials}</S.Avatar>}
            </S.BannerBadge>
          </S.Banner>
        </S.BannerWrap>

        <S.GettingStarted>
          <S.SectionHeading>
            <S.SectionTitle>Getting started</S.SectionTitle>
            <S.SectionSubtitle>Everything you need to start working with Appnext</S.SectionSubtitle>
          </S.SectionHeading>
          <S.LinkList>
            {GETTING_STARTED_LINKS.map((link) => (
              <S.LinkItem key={link.label} href={link.href}>
                <DocumentOutline />
                {link.label}
              </S.LinkItem>
            ))}
          </S.LinkList>
        </S.GettingStarted>
      </S.Left>

      <S.Divider />

      <S.Right>{renderSetup()}</S.Right>
    </S.Wrap>
  );
};
